import React from "react";
import { motion } from "framer-motion";
import {
  Cpu,
  LayoutGrid,
  Smartphone,
  Play,
  Check,
} from "lucide-react";
import { HOME } from "@/constants/testIds";

const services = [
  {
    icon: LayoutGrid,
    title: "Display",
    subtitle: "Advertising",
    text:
      "High-impact display ads across premium inventory with advanced targeting and creative optimization for maximum engagement and brand visibility.",
    bullets: [
      "Rich media formats",
      "Responsive ad units",
      "A/B testing capabilities",
    ],
  },
  {
    icon: Smartphone,
    title: "Mobile",
    subtitle: "Advertising",
    text:
      "Native mobile ad experiences optimized for iOS and Android platforms with location-based targeting and app-specific placements.",
    bullets: [
      "In-app advertising",
      "Location targeting",
      "Mobile-first creative",
    ],
  },
  {
    icon: Play,
    title: "Video",
    subtitle: "Advertising",
    text:
      "Engaging video ad formats including pre-roll, mid-roll, and outstream video with advanced viewability tracking and completion optimization.",
    bullets: [
      "VAST/VPAID support",
      "Viewability optimization",
      "Interactive video ads",
    ],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={HOME.servicesSection}
      className="relative py-32"
    >
      <div className="container-x">
        <SectionHeader
          eyebrow="Our Services"
          title={
            <>
              Comprehensive
              <br />
              <span className="aurora-text">Advertising Solutions</span>
            </>
          }
          subtitle="From programmatic advertising to direct sales, we provide end-to-end solutions that maximize your revenue potential and deliver exceptional campaign performance."
        />

        {/* Big programmatic card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="tile p-8 md:p-12 mb-8 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center"
        >
          <div>
            <div className="flex items-center gap-3 mb-4 text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              <Cpu className="w-4 h-4" />
              Featured
            </div>
            <h3 className="font-display text-4xl md:text-5xl font-bold mb-4">
              Programmatic
              <br />
              <span className="cyan-text">Advertising</span>
            </h3>
            <p className="text-white/65 mb-6 max-w-xl">
              Leverage the power of automated ad buying with our advanced
              programmatic platform. Real-time bidding, audience targeting, and
              optimization algorithms work together to deliver maximum ROI for
              your campaigns.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2.5 max-w-md">
              {[
                "Real-time bidding optimization",
                "Advanced audience segmentation",
                "Cross-device campaign management",
                "Fraud detection and brand safety",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-white/80">
                  <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-medium">Campaign Performance</span>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            </div>
            <div className="space-y-5">
              <PerfRow label="Impressions" value="2.4M" />
              <PerfRow label="CTR" value="3.2%" />
              <PerfRow label="Revenue" value="$12,450" highlight />
            </div>
            <SparkBars />
          </div>
        </motion.div>

        {/* 3-up services */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="tile p-8"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center mb-5">
                <s.icon className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h3 className="font-display text-2xl font-bold mb-2">
                {s.title}
                <span className="block text-base text-white/50 font-normal">
                  {s.subtitle}
                </span>
              </h3>
              <p className="text-sm text-white/65 mb-5">{s.text}</p>
              <ul className="space-y-1.5">
                {s.bullets.map((b) => (
                  <li key={b} className="text-xs text-white/60 flex gap-2">
                    <span className="text-[#00F0FF]">•</span> {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, align = "left" }) {
  return (
    <div
      className={`mb-16 ${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-3xl"}`}
    >
      {eyebrow && (
        <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
          {eyebrow}
        </div>
      )}
      <h2 className="font-display text-4xl md:text-6xl font-bold mb-5 leading-[1.05]">
        {title}
      </h2>
      {subtitle && (
        <p className="text-white/65 text-lg leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function PerfRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-3">
      <span className="text-sm text-white/55">{label}</span>
      <span
        className={`font-mono font-bold ${highlight ? "text-[#00F0FF] text-xl" : "text-white text-lg"}`}
      >
        {value}
      </span>
    </div>
  );
}

function SparkBars() {
  const heights = [40, 55, 35, 70, 60, 85, 45, 75, 90, 65, 55, 80];
  return (
    <div className="mt-6 flex items-end gap-1.5 h-16">
      {heights.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t bg-gradient-to-t from-[#0047FF]/40 to-[#00F0FF]"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}
